"use client";

import { ContentAnalytics } from "@/components/analytics/ContentAnalytics";
import { moduleContentView } from "@/lib/analytics/content-view";
import type { ReactNode } from "react";
import { Container, Section, SectionHeader } from "@/components/ui/Container";
import { ModuleGrid } from "@/components/modules/ModuleCard";
import { EventHero } from "@/components/modules/EventHero";
import { EventStartCard } from "@/components/modules/EventStartCard";
import { EventView } from "@/components/modules/EventView";
import { isPast } from "@/components/modules/cards/CardShell";
import { firstRecordingId } from "@/components/modules/eventRecordings";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import { isFreeToUnlock, moduleAccess } from "./paywall";

export function EventDetailClient({
  topSlot,
  sideSlot,
  lang,
  moduleId,
  module: m,
  enrolmentIds,
  isAuthed,
  initialSaved = false,
  isSubscription,
  library = false,
  cfHash = null,
}: {
  /** The template's `content_top` placement — rendered at the top of the content column. */
  topSlot?: ReactNode;
  /** The template's `content_right` placement — rendered in the card column, under the card. */
  sideSlot?: ReactNode;
  lang: Lang;
  moduleId: number;
  module: ModuleDetail;
  enrolmentIds: string[];
  isAuthed: boolean;
  /** Server-resolved bookmark state (loadSavedIds) — the schema has no per-module `saved` field. */
  initialSaved?: boolean;
  /** Portal runs in subscription mode (`portal.paymentModel`) — decides what
   *  the gate sells. Pass `portal.isSubscription`. */
  isSubscription: boolean;
  library?: boolean;
  /** Portal.cloudflareAccountHash — threads down to hero/poster image URLs. */
  cfHash?: string | null;
}) {
  const t = useDict();
  // The marketing header overlays the dark hero banner only for guests on the
  // public surface (authed users + /library render under the solid AppShell).
  const overlayHeader = !isAuthed && !library;
  // Feeds the similar-modules grid's card CTAs; the event's own gate is canView.
  const enrolledIds = new Set(enrolmentIds);
  const past = isPast(m.event?.dateTo);
  const live = m.event?.eventType === "live";

  // Recordings play like videos — no enrolment step: on the surface the
  // reader is on (members under /library, guests on the public twin), gated
  // on `canView`. The rows link in only when this reader may watch; the start
  // card offers the way in otherwise.
  const watchBase = langPath(
    lang,
    `${library ? "/library" : ""}/events/${moduleId}/${m.slug ?? ""}`,
  );
  const firstRecId = firstRecordingId(m.event);
  const canView = m.canView !== false;
  const watchHref = firstRecId ? `${watchBase}/${firstRecId}` : null;

  // `m.refType` is `event` here by construction: the route resolver
  // (lib/routes/resolve-detail.ts) 308s a wrong-type id to the route that
  // owns it before this renders, on the public and the /library surface.
  // A "not my type" fallback used to live here; it was unreachable.
  return (
    <>
      <ContentAnalytics view={moduleContentView(m)} />
      <EventHero
        lang={lang}
        module={m}
        library={library}
        overlayHeader={overlayHeader}
        cfHash={cfHash}
      />
      <Section className="!pt-5 !pb-12 animate-in fade-in duration-500 [animation-timing-function:var(--ease-smooth)]">
        <Container className="grid lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-start">
          {/* Abstract is rendered inside EventView's "About event" SectionCard
              as the editorial deck (matches CourseView/CourseDetailClient
              split — they belong to one block, not two floating elements). */}
          <div className="min-w-0">
            {topSlot}
            <EventView
              module={m}
              lang={lang}
              isAuthed={isAuthed}
              watchBase={canView ? watchBase : null}
            />
          </div>

          <div className="grid gap-4 lg:sticky lg:top-[calc(var(--marketing-header-h)+1rem)]">
            <EventStartCard
              lang={lang}
              moduleId={moduleId}
              event={m.event}
              access={moduleAccess({ ...m, isAuthed, isSubscription })}
              free={isFreeToUnlock(m)}
              isPast={past}
              isLive={live}
              isAuthed={isAuthed}
              initialLiked={m.liked === true}
              initialSaved={initialSaved}
              watchHref={watchHref}
              showSave={false}
            />
            {sideSlot}
          </div>
        </Container>
      </Section>

      {m.similarModules?.length ? (
        <Section className="bg-muted/40">
          <Container>
            <SectionHeader title={t.detail.similar} />
            <ModuleGrid
              modules={m.similarModules.slice(0, 6)}
              lang={lang}
              t={t}
              enrolledIds={enrolledIds}
              library={library}
            />
          </Container>
        </Section>
      ) : null}
    </>
  );
}
